import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../Components/Layouts/MainLayout/MainLayout';
import { useTranslation } from 'react-i18next';
import { getJobs } from '../../Services/Jobs';
import { useCallback, useEffect, useState } from 'react';
import SearchBar from '../../Components/SearchBar/SearchBar';
import { DateRangePicker } from '../../Components/Common/DateRangePicker/DateRangePicker';
import { useDebounce } from '../../Hooks/useDebounce';
import AutocompleteSelect from '../../Components/AutocompleteSelect/AutocompleteSelect';
import { getSkills } from '../../Services/Skills';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ClearFiltersButton } from '../../Components/Common/ClearFiltersButton/ClearFiltersButton';
import { getClients } from '../../Services/Client';
import {
  FilterStatusType,
  filterStatusTypes,
  JobStatusSelect,
} from '../../Components/StatusSelect/JobStatusSelect';
import { COUNT_ROWS_PER_PAGE } from '../../Types/constants';
import { Loader } from '../../Components/Loader/Loader';
import Paper from '@mui/material/Paper';
import { JobsTable } from './Components/JobsTable/JobsTable';

export const Jobs = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<FilterStatusType>(filterStatusTypes[0] as FilterStatusType);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [clearSearch, setClearSearch] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm);
  const debouncedSkillsSearchTerm = useDebounce(skillsSearchTerm);

  const [currentPage, setCurrentPage] = useState(0);
  const [jobList, setJobList] = useState<Job[]>([]);

  const title = t('jobs.title');

  const fetchJobs = useCallback(
    async ({ pageParam = 1 }) => {
      return await getJobs({
        page: pageParam,
        count: COUNT_ROWS_PER_PAGE,
        search: debouncedSearchTerm || undefined,
        status: status === 'all' ? undefined : status,
        fromCreationDate: startDate || undefined,
        toCreationDate: endDate || undefined,
        skills: selectedOptions.length > 0 ? selectedOptions : undefined,
        clientId: selectedClient?.id,
      });
    },
    [debouncedSearchTerm, status, startDate, endDate, selectedOptions, selectedClient],
  );

  const {
    data: jobsData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      'getJobs',
      debouncedSearchTerm,
      status,
      startDate,
      endDate,
      selectedOptions,
      selectedClient?.id,
    ],
    queryFn: fetchJobs,
    getNextPageParam: (lastPage) => {
      const nextPage = (lastPage.page || 0) + 1;
      return nextPage <= (lastPage.lastPage || 0) ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

  useEffect(() => {
    setCurrentPage(0);
    setJobList([]);
  }, [debouncedSearchTerm, status, startDate, endDate, selectedOptions, selectedClient]);

  useEffect(() => {
    const allJobs = jobsData?.pages.flatMap((page) => page.items) || [];
    setJobList(allJobs);
  }, [jobsData]);

  const totalItems = jobsData?.pages[jobsData.pages.length - 1]?.totalItems || 0;

  const { data: skills } = useQuery({
    queryKey: ['getSkills', debouncedSkillsSearchTerm],
    queryFn: () =>
      getSkills({
        page: 1,
        count: 10,
        name: debouncedSkillsSearchTerm,
      }),
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ['getClients'],
    queryFn: async () => {
      const response = await getClients({
        page: 1,
        count: 1000,
      });
      return response.items;
    },
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setStatus('All');
    setSkillsSearchTerm('');
    setSelectedOptions([]);
    setClearSearch(true);
    setSelectedClient(null);
    setJobList([]);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setClearSearch(false);
  };

  return (
    <div>
      <MainLayout
        onJobsInvalidateQuery={() =>
          queryClient.invalidateQueries({
            queryKey: [
              'getJobs',
              debouncedSearchTerm,
              status,
              startDate,
              endDate,
              selectedOptions,
              selectedClient?.id,
            ],
          })
        }
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <h1>{title}</h1>
          <ClearFiltersButton handleClearFilters={handleClearFilters} />
        </Box>
        <Accordion sx={{ marginBottom: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              margin: 0,
              '&.MuiAccordionSummary-root': {
                margin: 0,
              },
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
            }}
          >
            <div
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                marginTop: 15,
              }}
            >
              <SearchBar margin="0 0 15px 0" onSearch={handleSearch} clearSearch={clearSearch} />
              <JobStatusSelect
                containerMargin={2}
                handleChange={(newValue: FilterStatusType) => {
                  setStatus(newValue);
                }}
                status={status}
              />
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'flex', gap: '16px' }}>
              <DateRangePicker
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
              <AutocompleteSelect
                searchTerm={skillsSearchTerm}
                onSearchTermChange={(newValue: string) => setSkillsSearchTerm(newValue)}
                options={skills?.items.map((skill) => skill.name) ?? []}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
              />
              {!!clients && (
                <Autocomplete
                  sx={{ width: '380px' }}
                  id="client-autocomplete"
                  options={clients.length ? clients?.map((client) => client.name) : []}
                  onChange={(_, newValue) => {
                    const selected = clients?.find((client) => client.name === newValue);
                    setSelectedClient(selected || null);
                  }}
                  value={selectedClient?.name || ''}
                  renderInput={(params) => (
                    <TextField {...params} placeholder={t('common.client')} />
                  )}
                />
              )}
            </div>
          </AccordionDetails>
        </Accordion>

        {jobsData && (
          <Paper sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <JobsTable
              rows={jobList}
              loadMore={fetchNextPage}
              loading={isFetchingNextPage}
              hasMore={hasNextPage ?? false}
              totalItems={totalItems}
            />
          </Paper>
        )}
        <Loader isLoading={isLoading} />
      </MainLayout>
    </div>
  );
};
